import React from 'react';
import { useField } from 'informed';
import { useTranslation } from 'react-i18next';

import { RECEIVE_UPDATES_INFO, WhatsAppInfo } from '@nw/shared/config';

import { WhatsappIcon } from '../../../icons/WhatsappIcon';

import { CheckBox } from '../../CheckBox';

import {
  CheckBoxAndErrorMessageWrapper,
  CheckBoxHeading,
  CheckBoxLabelContainer,
  LabelIconWrapper,
  LabelText
} from './styledComponents';

interface InformedCheckboxProps {
  className?: string;
  labelClassName?: string;
  field: string;
  label?: string;
  validationSchemaObject?: any;
  onChange?: () => void;
  onBlur?: () => void;
  error?: string;
  touched?: boolean;
  isInnerHTML?: boolean;
  children?: any;
  checked?: boolean;
  fieldWrapperStyles?: any;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  hintClassName?: string;
  isMarginBottomZero?: boolean;
  isHidden?: boolean;
  shouldHideReceiveUpdates?: boolean;
  id?: string;
  shouldShowWhatsAppIcon?: boolean;
  shouldShowOnlyLabel?: boolean;
  checkboxClassName?: string;
}

export const InformedCheckbox = (props: InformedCheckboxProps) => {
  const {
    validationSchemaObject,
    label,
    className,
    labelClassName,
    isInnerHTML,
    error,
    field,
    fieldWrapperStyles,
    validateOnChange = true,
    validateOnBlur = false,
    hintClassName,
    isMarginBottomZero,
    isHidden,
    shouldHideReceiveUpdates,
    id,
    shouldShowWhatsAppIcon = true,
    shouldShowOnlyLabel,
    checkboxClassName
  } = props;

  const { t } = useTranslation();

  const { fieldState, fieldApi, render, userProps } = useField({
    ...props,
    validationSchema: validationSchemaObject?.fields?.[field],
    field,
    validateOnBlur,
    validateOnChange,
    error: error
  });
  const { setValue, setTouched } = fieldApi;
  const { error: fieldError, value } = fieldState;
  const { onChange, onBlur, ...rest } = userProps;

  const isWhatsAppInfo = field === WhatsAppInfo;

  const renderCheckboxHeading = (): React.ReactNode => {
    if (shouldHideReceiveUpdates) {
      return null;
    }

    const isReceiveUpdatesInfo = field === RECEIVE_UPDATES_INFO;

    if (isWhatsAppInfo || isReceiveUpdatesInfo) {
      const headingText = t('payment.receiveUpdates') as string;
      return <CheckBoxHeading>{headingText}</CheckBoxHeading>;
    }

    return null;
  };

  const renderLabel = (): React.ReactNode => {
    if (isWhatsAppInfo && shouldShowWhatsAppIcon) {
      return (
        <LabelIconWrapper>
          {/* Show WhatsApp icon on desktop (md:block) and mobile, matching Omega's MediaQuery at 768px */}
          <div className="md:block">
            <WhatsappIcon />
          </div>
          <div className="md:hidden">
            <WhatsappIcon />
          </div>
          {label && (
            <LabelText
              className={labelClassName}
              dangerouslySetInnerHTML={{
                __html: label
              }}
            />
          )}
        </LabelIconWrapper>
      );
    }
    return label ? (
      <LabelText
        className={labelClassName}
        dangerouslySetInnerHTML={{
          __html: label
        }}
      />
    ) : null;
  };

  const renderCheckBox = () => {
    // Convert value to boolean (informed can return string or boolean)
    const isSelected = Boolean(value);

    return (
      <CheckBox
        name={field}
        isSelected={isSelected}
        onChange={(e) => {
          setValue(e.target.checked);
          if (onChange) {
            // Match Omega signature: pass the checked value to onChange
            onChange(e.target.checked);
          }
        }}
        onBlur={(e) => {
          setTouched(true);
          if (onBlur) {
            onBlur();
          }
        }}
        error={fieldError}
        isDisabled={rest.isDisabled}
        className={`cursor-pointer flex-shrink-0 ${className || ''}`}
        checkboxClassName={checkboxClassName}
        labelClassName={labelClassName}
        hintClassName={hintClassName}
      >
        {isInnerHTML ? (
          props.children ? (
            props.children
          ) : (
            renderLabel()
          )
        ) : label ? (
          <LabelText className={labelClassName}>{label}</LabelText>
        ) : null}
      </CheckBox>
    );
  };

  const renderCheckBoxLabelContainer = () => {
    return (
      <CheckBoxLabelContainer className={labelClassName}>
        {isInnerHTML ? (
          props.children ? (
            props.children
          ) : (
            renderLabel()
          )
        ) : label ? (
          <LabelText className={labelClassName}>{label}</LabelText>
        ) : null}
      </CheckBoxLabelContainer>
    );
  };

  return render(
    <React.Fragment key={field}>
      {renderCheckboxHeading()}
      <CheckBoxAndErrorMessageWrapper
        css={fieldWrapperStyles}
        isHidden={isHidden}
        isMarginBottomZero={isMarginBottomZero}
      >
        {shouldShowOnlyLabel
          ? renderCheckBoxLabelContainer()
          : renderCheckBox()}
      </CheckBoxAndErrorMessageWrapper>
    </React.Fragment>
  );
};
